import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import useDigitalDownloads from '@/hooks/useDigitalDownloads';
import { listClientStoreAccounts, type ClientStoreAccount } from '@/lib/storeBalance';
import { buildAccountQrData, parseAccountQrData } from '@/lib/accountQr';
import QrScannerModal from '@/components/feature/QrScannerModal';
import QrModal from '@/pages/dashboard/credit/components/QrModal';
import InstallAppCard from '@/components/feature/InstallAppCard';
import { CLIENT_APPS, readInstalledApps, type ClientApp } from '@/lib/clientApps';
import type { ZifekUser } from '@/hooks/useAuth';

interface CommandeRow {
  id: number;
  titre: string | null;
  date_time: string | null;
  totalttc: string | null;
  sp: string | null;
  quantite: number | null;
  monaie: string | null;
  statut_com_client: string | null;
  statut_com_vendeur: string | null;
  details: string | null;
}

export type DashboardTab =
  | 'overview'
  | 'etudes'
  | 'orders'
  | 'courses'
  | 'payments'
  | 'files'
  | 'downloads'
  | 'store'
  | 'messages'
  | 'outfits'
  | 'closet'
  | 'profile'
  | 'favorites'
  | 'chat'
  | 'revenues';

interface Props {
  user: ZifekUser;
  onOpenChat: () => void;
  onOpenTab: (tab: DashboardTab) => void;
}

function money(value: number, currency: string): string {
  return `${value.toLocaleString('fr-FR')} ${currency || ''}`.trim();
}

function fmtDate(d: string | null): string {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  } catch {
    return d;
  }
}

const STATUS_LABELS: Record<string, string> = {
  'En attente': 'En attente',
  'en attente': 'En attente',
  'Confirmée': 'Confirmée',
  'Expédiée': 'Expédiée',
  'Livrée': 'Livrée',
  'Annulée': 'Annulée',
};

/**
 * Accueil du compte client — version épurée.
 * Uniquement l'essentiel : les chiffres clés, des raccourcis et les
 * dernières commandes. Tout le reste vit dans son onglet dédié.
 */
export default function OverviewTab({ user, onOpenChat, onOpenTab }: Props) {
  const [orders, setOrders] = useState<CommandeRow[]>([]);
  const [stores, setStores] = useState<ClientStoreAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const { downloads } = useDigitalDownloads(user?.id);
  const navigate = useNavigate();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [myQrOpen, setMyQrOpen] = useState(false);
  const [scanError, setScanError] = useState('');
  const [installedApps, setInstalledApps] = useState<ClientApp[]>([]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        // Rapprochement de la commande avec le compte connecté :
        // par id de compte, par email ou par téléphone.
        const filters: string[] = [];
        if (user.id != null) filters.push(`user_id.eq.${user.id}`);
        if (user.email) {
          filters.push(`email.eq.${user.email}`);
          filters.push(`user_id.eq.${user.email}`);
        }
        if (user.telephone) {
          filters.push(`tel.eq.${user.telephone}`);
          filters.push(`user_id.eq.${user.telephone}`);
        }

        let ordersQuery = supabase
          .from('commande')
          .select('id, titre, date_time, totalttc, sp, quantite, monaie, statut_com_client, statut_com_vendeur, details')
          .order('date_time', { ascending: false })
          .limit(8);
        if (filters.length > 0) ordersQuery = ordersQuery.or(filters.join(','));

        const { data: ordersData } = await ordersQuery;
        if (!cancelled) setOrders((ordersData as CommandeRow[]) || []);

        const storeRows = await listClientStoreAccounts(user.id);
        if (!cancelled) setStores(storeRows);
      } catch {
        /* en cas d'erreur, on affiche les sections vides */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let alive = true;
    void (async () => {
      try {
        const slugs = await readInstalledApps(user.id);
        if (alive) setInstalledApps(CLIENT_APPS.filter((app) => slugs.includes(app.slug)));
      } catch {
        if (alive) setInstalledApps([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user]);

  const currency = user.monaie || 'MAD';
  const { equivalence } = useDisplayCurrency(currency);
  const totalSpent = orders.reduce((sum, o) => {
    const v = parseFloat(String(o.totalttc ?? o.sp ?? '0'));
    return sum + (isNaN(v) ? 0 : v);
  }, 0);

  const stats: { icon: string; label: string; value: string | number; color: string; tab?: DashboardTab }[] = [
    { icon: 'ri-shopping-bag-3-line', label: 'Commandes', value: orders.length, color: 'bg-accent-100 text-accent-700', tab: 'orders' },
    { icon: 'ri-money-dollar-circle-line', label: 'Total dépensé', value: money(totalSpent, currency), color: 'bg-primary-100 text-primary-700' },
    { icon: 'ri-download-cloud-2-line', label: 'Téléchargements', value: downloads.length, color: 'bg-secondary-100 text-secondary-700', tab: 'downloads' },
    { icon: 'ri-store-2-line', label: 'Soldes magasin', value: stores.length, color: 'bg-background-200/80 text-foreground-700', tab: 'store' },
  ];

  const quickAccess: { icon: string; label: string; color: string; onClick: () => void }[] = [
    { icon: 'ri-book-open-line', label: "Mon espace d'études", color: 'bg-accent-100 text-accent-700', onClick: () => onOpenTab('etudes') },
    { icon: 'ri-shopping-bag-3-line', label: 'Mes commandes', color: 'bg-accent-100 text-accent-700', onClick: () => onOpenTab('orders') },
    { icon: 'ri-wallet-3-line', label: 'Paiements', color: 'bg-primary-100 text-primary-700', onClick: () => onOpenTab('payments') },
    { icon: 'ri-download-cloud-2-line', label: 'Téléchargements', color: 'bg-secondary-100 text-secondary-700', onClick: () => onOpenTab('downloads') },
    { icon: 'ri-megaphone-line', label: 'Missions', color: 'bg-accent-100 text-accent-700', onClick: () => onOpenTab('revenues') },
    { icon: 'ri-chat-smile-3-line', label: 'Messages', color: 'bg-secondary-100 text-secondary-700', onClick: () => onOpenTab('messages') },
    { icon: 'ri-heart-3-line', label: 'Favoris', color: 'bg-primary-100 text-primary-700', onClick: () => onOpenTab('favorites') },
    { icon: 'ri-graduation-cap-line', label: 'Mes formations', color: 'bg-accent-100 text-accent-700', onClick: () => onOpenTab('courses') },
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Bienvenue */}
      <div>
        <h1 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
          Bonjour, {user.name?.split(' ')[0] || user.user_name}
        </h1>
        <p className="text-sm text-foreground-500 mt-1">
          Voici l&apos;essentiel de votre compte.
        </p>
      </div>

      {/* Mes QR codes Zifek */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <button
          type="button"
          onClick={() => { setScanError(''); setScannerOpen(true); }}
          className="flex items-center gap-3 bg-foreground-950 text-background-50 rounded-lg p-4 cursor-pointer hover:bg-foreground-800 transition-colors text-left"
        >
          <span className="w-11 h-11 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
            <i className="ri-qr-scan-2-line text-xl"></i>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold font-heading">Scanner un QR code</span>
            <span className="block text-[11px] text-background-50/70 mt-0.5">
              Payer, envoyer ou demander de l&apos;argent
            </span>
          </span>
          <i className="ri-arrow-right-line text-base flex-shrink-0"></i>
        </button>

        <button
          type="button"
          onClick={() => setMyQrOpen(true)}
          className="flex items-center gap-3 bg-background-50 border border-background-200/50 rounded-lg p-4 cursor-pointer hover:border-foreground-300/60 transition-colors text-left"
        >
          <span className="w-11 h-11 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center flex-shrink-0">
            <i className="ri-qr-code-line text-xl"></i>
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-bold font-heading text-foreground-950">Mon code QR Zifek</span>
            <span className="block text-[11px] text-foreground-500 mt-0.5">
              À faire scanner par un commerce ou un proche
            </span>
          </span>
          <i className="ri-arrow-right-line text-base text-foreground-300 flex-shrink-0"></i>
        </button>
      </div>

      {scanError && (
        <div className="px-4 py-2.5 rounded-lg bg-red-50 border border-red-200 text-xs text-red-700 flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{scanError}
        </div>
      )}

      {/* Chiffres clés */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map((s) => (
          <button
            type="button"
            key={s.label}
            onClick={s.tab ? () => onOpenTab(s.tab as DashboardTab) : undefined}
            className={`text-left bg-background-50 border border-background-200/50 rounded-lg p-4 ${
              s.tab ? 'cursor-pointer hover:border-foreground-300/60 transition-colors' : 'cursor-default'
            }`}
          >
            <div className={`w-9 h-9 rounded-md ${s.color} flex items-center justify-center mb-2.5`}>
              <i className={`${s.icon} text-base`}></i>
            </div>
            <p className="text-base md:text-lg font-bold font-heading text-foreground-950 truncate">{s.value}</p>
            <p className="text-xs text-foreground-500 mt-0.5">{s.label}</p>
          </button>
        ))}
      </div>

      {/* Accès rapide */}
      <div className="bg-background-50 border border-background-200/50 rounded-lg p-4">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {quickAccess.map((q) => (
            <button
              type="button"
              key={q.label}
              onClick={q.onClick}
              className="flex items-center gap-2.5 px-3 py-2.5 rounded-md text-left cursor-pointer hover:bg-background-100 transition-colors"
            >
              <span className={`w-8 h-8 rounded-md ${q.color} flex items-center justify-center flex-shrink-0`}>
                <i className={`${q.icon} text-sm`}></i>
              </span>
              <span className="text-sm font-medium text-foreground-800 truncate">{q.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Mes applications installées */}
      <div className="bg-background-50 border border-background-200/50 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-background-200/50">
          <h2 className="text-sm font-bold font-heading text-foreground-950">Mes applications</h2>
          <button
            type="button"
            onClick={() => navigate('/mon-compte/appstore')}
            className="text-xs font-medium text-foreground-500 hover:text-foreground-900 cursor-pointer whitespace-nowrap"
          >
            Voir plus <i className="ri-arrow-right-s-line"></i>
          </button>
        </div>

        {installedApps.length === 0 ? (
          <div className="px-5 py-7 text-center">
            <div className="w-11 h-11 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-apps-2-line text-lg text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-600">Aucune application installée.</p>
            <p className="text-xs text-foreground-400 mt-1">Notes, planning, quiz… choisissez vos outils dans l&apos;App Store.</p>
            <button
              type="button"
              onClick={() => navigate('/mon-compte/appstore')}
              className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-foreground-950 text-background-50 text-xs font-semibold whitespace-nowrap cursor-pointer hover:bg-foreground-800"
            >
              <i className="ri-store-3-line"></i>
              Ouvrir l&apos;App Store
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 p-4">
            {installedApps.map((app) => (
              <button
                type="button"
                key={app.slug}
                onClick={() => navigate(app.route)}
                className="flex items-center gap-2.5 p-3 rounded-lg border border-background-200/50 hover:border-foreground-300/60 transition-colors text-left cursor-pointer"
              >
                <span className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-900 flex items-center justify-center flex-shrink-0">
                  <i className={`${app.icon} text-sm`}></i>
                </span>
                <span className="min-w-0">
                  <span className="block text-xs font-semibold text-foreground-900 truncate">{app.name}</span>
                  <span className="block text-[10px] text-foreground-400 truncate">
                    {app.offline ? 'Hors ligne' : app.category}
                  </span>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Installation PWA discrète */}
      <InstallAppCard variant="client" />

      {/* Dernières commandes */}
      <div className="bg-background-50 border border-background-200/50 rounded-lg overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-background-200/50">
          <h2 className="text-sm font-bold font-heading text-foreground-950">Dernières commandes</h2>
          {orders.length > 0 && (
            <button
              onClick={() => onOpenTab('orders')}
              className="text-xs font-medium text-foreground-500 hover:text-foreground-900 cursor-pointer whitespace-nowrap"
            >
              Voir tout
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-10">
            <i className="ri-loader-4-line animate-spin text-lg text-foreground-400"></i>
          </div>
        ) : orders.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <div className="w-11 h-11 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
              <i className="ri-shopping-bag-3-line text-lg text-foreground-400"></i>
            </div>
            <p className="text-sm text-foreground-500">Aucune commande pour le moment.</p>
            <p className="text-xs text-foreground-400 mt-1">Vos achats sur les boutiques Zifek apparaîtront ici.</p>
          </div>
        ) : (
          <div className="divide-y divide-background-200/40">
            {orders.slice(0, 4).map((o) => {
              const value = parseFloat(String(o.totalttc ?? o.sp ?? '0'));
              const status = o.statut_com_client || o.statut_com_vendeur || 'En cours';
              return (
                <div key={o.id} className="flex items-center gap-3 px-5 py-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground-900 truncate">
                      {o.titre || o.details || `Commande #${o.id}`}
                    </p>
                    <p className="text-xs text-foreground-400">
                      {fmtDate(o.date_time)} · {STATUS_LABELS[status] || status}
                    </p>
                  </div>
                  <span className="text-sm font-semibold text-foreground-900 whitespace-nowrap">
                    {money(isNaN(value) ? 0 : value, o.monaie || currency)}
                  </span>
                  {equivalence(isNaN(value) ? 0 : value, o.monaie || currency) && (
                    <span className="hidden sm:block text-[10px] text-foreground-400 whitespace-nowrap">
                      {equivalence(isNaN(value) ? 0 : value, o.monaie || currency)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Assistant IA */}
      <button
        onClick={onOpenChat}
        className="w-full text-left bg-foreground-950 text-background-50 rounded-lg p-5 flex items-center gap-4 cursor-pointer hover:bg-foreground-800 transition-colors"
      >
        <div className="w-11 h-11 rounded-full bg-accent-500 flex items-center justify-center flex-shrink-0">
          <i className="ri-robot-2-line text-white text-lg"></i>
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-bold font-heading text-sm">Assistant IA Zifek</p>
          <p className="text-xs text-background-50/70 mt-0.5">
            Cherchez un produit, un service ou réservez un rendez-vous.
          </p>
        </div>
        <i className="ri-arrow-right-line text-base flex-shrink-0"></i>
      </button>

      {scannerOpen && (
        <QrScannerModal
          title="Scanner un QR Zifek"
          hint="Scannez le code d'un commerce ou d'un client Zifek."
          onResult={(raw) => {
            const payload = parseAccountQrData(raw);
            if (!payload) {
              setScanError('Code QR non reconnu.');
              setScannerOpen(false);
              return;
            }
            setScannerOpen(false);
            navigate(`/mon-compte/scan?type=${payload.type}&id=${payload.id}`);
          }}
          onClose={() => setScannerOpen(false)}
        />
      )}

      {myQrOpen && (
        <QrModal
          title="Mon code QR Zifek"
          subtitle={user.name || user.user_name}
          data={buildAccountQrData('client', user.id)}
          onClose={() => setMyQrOpen(false)}
        />
      )}
    </div>
  );
}